import { useEffect, useState } from "react";
import { DataTable } from "mantine-datatable";
import {  ICurrencyRates, IDonation, IWithdrawal } from "../types";
import { Avatar, Group, Text, Paper, Grid,  Title, Loader, Notification } from "@mantine/core";
import { convertCurrency, isErrorObject, toTitleCase } from "../utils/utils";
import { makeGetRequest } from "../services/httpHelper";
import ErrorHandler from "../services/ErrorHandler";
import { IconAlertCircle } from "@tabler/icons-react";

const PAGE_SIZE = 10;

interface DonationsTableProps {
  donations: IDonation[];
  campaign_currency: string;
  Withdrawals:IWithdrawal[]
}

const AdminCampaignDonationTable: React.FC<DonationsTableProps> = ({
  donations,
  campaign_currency,
  Withdrawals
}) => {
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState(donations.slice(0, PAGE_SIZE));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [exchangeRates, setExchangeRates] = useState<ICurrencyRates>();

  // Fetch exchange rates
  const fetchExchangeRates = async () => {
    const endpoint = "latest-currency-exchange-rates";
    setIsLoading(true)
    const res = await makeGetRequest(endpoint);
    if (isErrorObject(res)) {
      setError(ErrorHandler(res));
    } else {
      setExchangeRates(res.data);
      setIsLoading(false)
    }
  };

  // Currency conversion handler
  const handleCurrencyConversion = (amount: number, fromCurrency: string, toCurrency: string) => {
    return convertCurrency(amount, fromCurrency, toCurrency, exchangeRates?.rates || {});
  };

  // Calculate totals
  const totalDonations = donations.reduce<number>((acc, d) => {
    const amount = campaign_currency === d.donation_currency
      ? Number(d.donation_amount)
      : handleCurrencyConversion(Number(d.donation_amount), d.donation_currency || '', campaign_currency)?.amount || 0;
    return acc + amount;
  }, 0);

  

  const totalWithdrawals = Withdrawals.reduce<number>((acc, d) => {
    const amount = handleCurrencyConversion(Number(d.amount || 0), d.currency || campaign_currency, campaign_currency)?.amount || 0;
    return acc + amount;
  }, 0);

  const serviceFees = donations.reduce<number>((acc, d) => {
    const fees = (Number(d.processor_fee) + Number(d.service_fee));
    const convertedFee = handleCurrencyConversion(fees, d.donation_currency || '', campaign_currency)?.amount || 0;
    return acc + convertedFee;
  }, 0);

  const currentBalance = totalDonations - (totalWithdrawals + serviceFees);

  useEffect(() => {

 
    fetchExchangeRates();
  }, []);

  useEffect(() => {
    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE;
    setRecords(donations.slice(from, to));
  }, [page, donations]);

  return (
    <>
      {isLoading && <Loader />}
      {error && (
        <Notification icon={<IconAlertCircle size={18} />} color="red" mb="lg">
          {error}
        </Notification>
      )}


<Grid gutter="lg">
  <Grid.Col xs={12} sm={6} md={3}>
    <Paper withBorder p="md" radius="md">
      <Title order={4}>Total Donations</Title>
      <Text weight={700} size="xl">
        {totalDonations.toLocaleString(undefined, {
          style: 'currency',
          currency: campaign_currency,
        })}
      </Text>
    </Paper>
  </Grid.Col>

  <Grid.Col xs={12} sm={6} md={3}>
    <Paper withBorder p="md" radius="md">
      <Title order={4}>Service Fees</Title>
      <Text weight={700} size="xl" color="orange">
        {serviceFees.toLocaleString(undefined, {
          style: 'currency',
          currency: campaign_currency,
        })}
      </Text>
    </Paper>
  </Grid.Col>

  <Grid.Col xs={12} sm={6} md={3}>
    <Paper withBorder p="md" radius="md">
      <Title order={4}>Total Withdrawals</Title>
      <Text weight={700} size="xl" color="red">
        {totalWithdrawals.toLocaleString(undefined, {
          style: 'currency',
          currency: campaign_currency,
        })}
      </Text>
    </Paper>
  </Grid.Col>

  <Grid.Col xs={12} sm={6} md={3}>
    <Paper withBorder p="md" radius="md">
      <Group position="apart">
        <div>
          <Title order={4}>Current Balance</Title>
          <Text
            weight={700}
            size="xl"
            color={currentBalance > 0 ? 'green' : 'gray'}
          >
            {currentBalance.toLocaleString(undefined, {
              style: 'currency',
              currency: campaign_currency,
            })}
          </Text>
        </div>
      </Group>
    </Paper>
  </Grid.Col>
</Grid>


      <DataTable
        columns={[
          { accessor: "Donor", render: ({ first_name, last_name, donor_anonymous }: IDonation) => (
            <Group>
              <Avatar src="https://www.freepik.com/vectors/profile-placeholder" alt="profile" size="sm" radius="xl" />
              <Text>{donor_anonymous ? "Anonymous" : `${toTitleCase(first_name)} ${toTitleCase(last_name)}`}</Text>
            </Group>
          )},
          { accessor: "donation_currency", title: "Donation Currency" },
          { accessor: "donation_amount", title: "Original Amount" },
          { accessor: "settled_amount", title: `Settled Amount (${campaign_currency})`, render: (d: IDonation) => (
            handleCurrencyConversion(Number(d.donation_amount), d.donation_currency || '', campaign_currency)?.amount.toLocaleString(undefined, { style: "currency", currency: campaign_currency })
          )}
        ]}
        records={records}
        totalRecords={donations.length}
        recordsPerPage={PAGE_SIZE}
        page={page}
        onPageChange={setPage}
        highlightOnHover
        verticalSpacing="sm"
        striped
      />
    </>
  );
};

export default AdminCampaignDonationTable;